import { createStore, useStore } from 'zustand'

/**
 * Cross-tree state for the by-post "New comments available" pill: the posts a
 * channel backfill added unreplied comments to, and that the open list has not
 * loaded yet.
 *
 * A backfill event carries a channel and a count, never a post, so the event
 * alone cannot say whether the selected post gained comments. The per-post
 * counts can: they are refetched when a backfill finishes, and a post whose
 * unreplied count grew across that refetch holds comments no per-comment event
 * announced.
 *
 * The two sides sit in different parts of the tree — the counts live in the
 * by-post view's counts provider, the pill lives in the page header — so this
 * module carries the report between them.
 */
type BackfilledPostCommentsState = {
  postIds: ReadonlySet<string>
  markPost: (postId: string) => void
  clearPost: (postId: string) => void
}

const EMPTY_POST_IDS: ReadonlySet<string> = new Set()

const backfilledPostCommentsStore = createStore<BackfilledPostCommentsState>()(
  (set, get) => ({
    postIds: EMPTY_POST_IDS,

    markPost: (postId): void => {
      const current = get().postIds
      if (current.has(postId)) return
      set({ postIds: new Set([...current, postId]) })
    },

    clearPost: (postId): void => {
      const current = get().postIds
      if (!current.has(postId)) return
      const next = new Set(current)
      next.delete(postId)
      set({ postIds: next.size === 0 ? EMPTY_POST_IDS : next })
    },
  }),
)

/** Report that a backfill added unreplied comments to this post. */
export const markPostGainedBackfilledComments = (postId: string): void =>
  backfilledPostCommentsStore.getState().markPost(postId)

/** Drop the report, e.g. once the post's comments have been loaded. */
export const clearPostBackfilledComments = (postId: string): void =>
  backfilledPostCommentsStore.getState().clearPost(postId)

/** Read the reported posts without subscribing. */
export const getPostsWithBackfilledComments = (): ReadonlySet<string> =>
  backfilledPostCommentsStore.getState().postIds

/**
 * Whether a backfill added unreplied comments to this post. Always false
 * without a post, so the flat list view never reads a per-post report.
 */
export const usePostHasBackfilledComments = (postId?: string): boolean =>
  useStore(backfilledPostCommentsStore, (state) =>
    postId ? state.postIds.has(postId) : false,
  )
